import { DBO_TM } from '@op/utils/format';
import moment from 'moment';
import {
  IDateAllCommonSelectType,
  IDateCommonSelectType,
  TSecondOptionsItem,
  TComponentConfigType,
  TFilterKeyType,
} from '../types';
import { has, isEqual } from 'lodash-es';

/**
 * 根据 dependencyOrder 对 renderConfig 进行排序，用于处理数据依赖关系
 * @param renderConfig 原始配置
 * @param dependencyOrder 数据依赖顺序，默认为 keys（即 UI 显示顺序）
 */
export function sortConfigByDependencyOrder(
  renderConfig: TComponentConfigType<any>[],
  dependencyOrder?: TFilterKeyType[],
): TComponentConfigType<any>[] {
  if (!dependencyOrder || dependencyOrder.length === 0) {
    return renderConfig;
  }

  // 构建 key -> config 的映射
  const configMap = new Map<TFilterKeyType, TComponentConfigType<any>>();
  renderConfig.forEach((config) => {
    configMap.set(config.key, config);
  });

  // 按 dependencyOrder 顺序返回配置
  const sorted: TComponentConfigType<any>[] = [];
  dependencyOrder.forEach((key) => {
    const config = configMap.get(key);
    if (config) {
      sorted.push(config);
    }
  });

  // 添加 dependencyOrder 中未包含的配置（保持兼容）
  renderConfig.forEach((config) => {
    if (!dependencyOrder.includes(config.key)) {
      sorted.push(config);
    }
  });

  return sorted;
}

export function getObjKeysValue(
  keys: { [key: string]: string },
  filterValue: any,
) {
  const obj: any = {};
  Object.keys(keys).forEach(key => {
    const vKey = keys[key];
    if (filterValue?.[vKey]) {
      obj[key] = filterValue[vKey];
    }
  });

  return obj;
}

function dealwithTimesValueKeysObj(
  obj: { start_time: string; end_time: string },
  valueKeys: {
    start_time: string;
    end_time: string;
  },
) {
  const { start_time: sKey, end_time: eKey } = valueKeys;

  return {
    [sKey]: obj.start_time,
    [eKey]: obj.end_time,
  };
}

/**
 * 判断日期是否有效, 并返回对应的默认值
 * @param filterValue
 * @returns
 */
export function getDateDefaultValue(
  filterValue: any,
  props: IDateCommonSelectType,
  isSingle = false,
) {
  const { valueKeys } = props;

  const obj: any = getObjKeysValue(valueKeys, filterValue);

  // 如果日期有效, 则不设置默认值
  const { start_time, end_time, date_type } = obj;
  if (start_time && end_time && date_type) {
    if (moment(start_time).isValid() && moment(end_time).isValid()) {
      if (!isSingle) {
        return {
          ...dealwithTimesValueKeysObj({ start_time, end_time }, valueKeys),
          date_type,
        };
      }

      return {
        ...dealwithTimesValueKeysObj(
          DBO_TM.serverTimeObj([end_time, end_time], 'daily'),
          valueKeys,
        ),
        date_type,
      };
    }
  }
  // 如果日期无效, 则设置默认值为今天
  return {
    ...dealwithTimesValueKeysObj(DBO_TM.today('daily'), valueKeys),
    date_type: 'daily',
  };
}

/**
 * 判断日期是否有效, 并返回对应的默认值
 * @param filterValue
 * @returns
 */
export function getAllDateDefaultValue(
  filterValue: any,
  props: IDateAllCommonSelectType,
  isSingle = false,
) {
  const { valueKeys } = props;

  const obj: any = getObjKeysValue(valueKeys, filterValue);

  // 如果日期有效, 则不设置默认值
  const { start_time, end_time } = obj;
  if (start_time && end_time) {
    if (moment(start_time).isValid() && moment(end_time).isValid()) {
      if (!isSingle) {
        return dealwithTimesValueKeysObj({ start_time, end_time }, valueKeys);
      }

      return dealwithTimesValueKeysObj(
        DBO_TM.serverTimeObj([end_time, end_time], 'daily'),
        valueKeys,
      );
    }
  }

  // 如果日期无效, 则设置空对象
  return {
    [valueKeys.start_time]: undefined,
    [valueKeys.end_time]: undefined,
  };
}

/**
 * 获取单选类型的默认值
 * @param filterValue
 * @returns
 */
export function getSingleSelectDefaultValue(
  filterValue: any,
  key: string,
  options: { value: string }[],
) {
  let realValue = options?.[0]?.value;
  const value = filterValue[key];
  if (value) {
    const item = options.find(item => item.value === value);
    if (item) realValue = item.value;
  }

  return { [key]: realValue };
}


/**
 * 获取多选类型的默认值
 * @param filterValue
 * @returns
 */
export function getMultipleSelectDefaultValue(
  filterValue: any,
  key: string,
  options: { value: string }[],
  defaultValue: string[],
) {
  const value = filterValue[key];
  let realValue = defaultValue;
  if (Array.isArray(value) && value.length > 0) {
    const items = options.filter(item => value.includes(item.value));
    if (items.length > 0) realValue = items.map(item => item.value);
  }

  return { [key]: realValue };
}

/**
 * 获取多选类型的默认值
 * @param filterValue
 * @returns
 */
export function getMultipleSelectNoneDefaultValue(
  filterValue: any,
  key: string,
  options: { value: string }[],
  allValue: string[]
) {
  const value = filterValue[key];
  let realValue: string[] = [];
  if (Array.isArray(value) && value.length > 0) {
    const items = options.filter(item => value.includes(item.value));
    if (items.length > 0) realValue = items.map(item => item.value);
  } else if (!Array.isArray(value)) {
    realValue = allValue;
  }

  return { [key]: realValue };
}

/**
 * 获取多选类型的默认值（支持全不选/全选特殊处理，二级结构）
 * - 全选：返回 { type: "market", regions: [], markets: ["global"] }
 * - 全不选：返回 { type: "market", regions: [], markets: [] }
 * - 部分选择：返回 { type: "market", regions: [父项values], markets: [选中值] }
 * @param filterValue
 * @param key 字段名
 * @param options 可选项列表（二级结构）
 * @param unselectAllValue 全不选的特殊值，默认为 "-99999"
 * @returns
 */
export function getMultipleSelectDefaultValueWithSpecialCase(
  filterValue: any,
  key: string,
  options: TSecondOptionsItem[],
  defaultValue: string[],
  unselectAllValue = '-99999',
) {
  const value = filterValue[key] || [];

  if (!Array.isArray(value)) {
    if (has(value, 'regions') && has(value, 'markets') && has(value, 'type')) {
      return value;
    }
  }

  // 处理全不选的特殊情况：["-99999"] 表示全不选
  if (
    Array.isArray(value) &&
    value.length === 1 &&
    value[0] === unselectAllValue
  ) {
    return {
      [key]: {
        type: 'market',
        regions: [],
        markets: [],
      },
    };
  }

  // 空数组表示全选
  if (Array.isArray(value) && value.length === 0) {
    const allRegions: string[] = [];
    const allMarkets: string[] = [];

    for (const option of options) {
      allRegions.push(option.value);
      const children = option.children || [];
      for (const child of children) {
        allMarkets.push(child.value);
      }
    }

    return {
      [key]: {
        type: 'market',
        regions: allRegions,
        markets: allMarkets,
      },
    };
  }

  // 正常处理多选逻辑（从二级 children 中过滤，同时收集父项）
  if (Array.isArray(value) && value.length > 0) {
    const parentValues = new Set<string>();
    const validValues: string[] = [];

    for (const option of options) {
      const children = option.children || [];
      for (const child of children) {
        if (value.includes(child.value)) {
          parentValues.add(option.value);
          validValues.push(child.value);
        }
      }
    }

    // 如果没有找到匹配项，使用默认值
    if (validValues.length === 0) {
      return {
        [key]: {
          type: 'market',
          regions: [],
          markets: defaultValue,
        },
      };
    }

    return {
      [key]: {
        type: 'market',
        regions: Array.from(parentValues),
        markets: validValues,
      },
    };
  }

  // 默认情况
  return {
    [key]: {
      type: 'market',
      regions: [],
      markets: defaultValue,
    },
  };
}
